/**
 * #360 / #686 / #691 — LA REGLA DE LOS DATOS DE PRUEBA, DEL LADO WEB.
 *
 * Gemelo de `mcp-server/src/services/testDataScope.js`. Son DOS archivos y no
 * uno por una razón mecánica, no de gusto: CRA sólo compila lo que cuelga de
 * `src/`, así que este árbol no puede importar del otro. Una copia por ÁRBOL —
 * que es distinto de lo que #691 castiga, que era una copia por TOOL.
 *
 * Este lado es más chico a propósito: acá no hay envoltorio de MCP ni modelo
 * leyendo JSON, hay una UI. Lo que sí se comparte es la decisión: **filtrar
 * está bien; callar que filtraste, no.** `listProjects` de este árbol filtraba
 * desde #360 y devolvía `{ data, count }` sin decir una palabra de lo que había
 * sacado — la misma mudez que #686 midió del lado del agente, sólo que la
 * persona que la sufre es Jorge frente a un buscador que dice "sin resultados".
 *
 * Lo que este módulo devuelve es un objeto para que la capa de servicio lo
 * pase hacia arriba (`testDataFilter`). Renderizarlo es de la UI, y hoy no lo
 * hace nadie: ver "qué queda sin cubrir" en el commit que lo introdujo.
 */

export const INCLUDE_TEST_DATA_PARAM = 'includeTestData';

/**
 * Los ids de los proyectos marcados como fixture. Fail-open: si la lectura
 * falla se devuelve la lista vacía y `disponible:false`, y el aviso lo declara.
 */
export const fetchTestProjectIds = async (supabase) => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('id')
      .eq('is_test_data', true);
    if (error) return { ids: [], disponible: false };
    return { ids: (data || []).map((p) => p.id).filter(Boolean), disponible: true };
  } catch {
    return { ids: [], disponible: false };
  }
};

/** `not(...in...)` con lista vacía es error de sintaxis de PostgREST. */
export const excluirPorProyectoDePrueba = (query, ids) => {
  if (!ids || ids.length === 0) return query;
  return query.not('project_id', 'in', `(${ids.join(',')})`);
};

/**
 * Cuántas filas ocultó el filtro: el MISMO universo con el filtro invertido y
 * sin traer filas. Envuelto en try/catch a propósito — fail-open. Si el conteo
 * no se puede hacer, la lista se devuelve igual y el aviso dice `hidden: null`;
 * romper la pantalla por no poder contar lo oculto sería peor que el silencio
 * que este módulo vino a arreglar.
 *
 * @param {Function} construirQuery - `() => query` con los MISMOS filtros de
 *   negocio que el listado. Sin eso el contador mide otro universo.
 * @returns {Promise<number|null>}
 */
export const contarOcultas = async (construirQuery) => {
  try {
    const { count, error } = await construirQuery();
    if (error) return null;
    return typeof count === 'number' ? count : null;
  } catch {
    return null;
  }
};

/**
 * El bloque estructurado que acompaña a TODA lista que pudo filtrar. Va
 * siempre, incluso con `hidden: 0`: sin él, la ausencia del bloque no
 * distingue entre "no había fixtures" y "esta lista no aprendió la regla".
 */
export const avisoDatosDePrueba = ({ include, ocultas, sujeto, disponible = true }) => {
  const parameter = INCLUDE_TEST_DATA_PARAM;

  if (include) {
    return {
      applied: false,
      hidden: 0,
      parameter,
      message:
        `Se están incluyendo también los resultados de ${sujeto} marcados como datos de prueba (${parameter}: true). `
        + 'Cada fila declara el flag is_test_data; donde valga true es un fixture de QA, no un dato real.',
    };
  }

  if (!disponible) {
    return {
      applied: false,
      hidden: null,
      parameter,
      message:
        `No se pudo aplicar el filtro de datos de prueba, así que esta lista de ${sujeto} puede mezclar `
        + 'fixtures de QA con datos reales. Revisá el flag is_test_data antes de tratar cualquier fila como real.',
    };
  }

  if (ocultas === null) {
    return {
      applied: true,
      hidden: null,
      parameter,
      message:
        `Se excluyeron los resultados de ${sujeto} marcados como datos de prueba (#360), pero no se pudo contar cuántos. `
        + `Una lista vacía o corta acá NO significa que no exista lo que buscás: pasá ${parameter}: true para verlos.`,
    };
  }

  if (ocultas === 0) {
    return {
      applied: true,
      hidden: 0,
      parameter,
      // #740 — LA FRASE ACOTADA A LO QUE ESTE BLOQUE SABE.
      //
      // Decía "no se ocultó nada", a secas. Literalmente cierto —el filtro de
      // fixtures no ocultó nada— y funcionalmente falso cuando lo que vació la
      // lista fue otra cosa: medido en #740, `list_projects({ search:
      // 'remodelación' })` devuelve `[]` por la tilde y se lleva ESTE bloque
      // encima, diciéndole al modelo que el vacío es de fiar.
      //
      // O sea que el bloque que existe para que el modelo NO concluya de más
      // ante un vacío estaba autorizando la conclusión. Un modelo que hubiera
      // dudado del `[]`, con esa frase deja de dudar.
      //
      // El bloque controla UN motivo. Ahora afirma sobre ese y declara que hay
      // otros, en vez de hablar por todos.
      message:
        `El filtro de datos de prueba no ocultó ningún resultado de ${sujeto}: ninguno de los que `
        + 'coinciden con tu consulta está marcado como fixture. OJO — esto habla SÓLO de ese filtro. '
        + 'Si esperabas encontrar algo y no está, la lista puede venir corta o vacía por otra razón '
        + '(cómo está escrito el término de búsqueda, los otros filtros, o la paginación), y este '
        + 'aviso no dice nada sobre ésas.',
    };
  }

  return {
    applied: true,
    hidden: ocultas,
    parameter,
    message:
      `OJO: ${ocultas} ${ocultas === 1 ? 'resultado' : 'resultados'} de ${sujeto} que ${ocultas === 1 ? 'coincide' : 'coinciden'} `
      + `con tu consulta ${ocultas === 1 ? 'tiene' : 'tienen'} is_test_data = true y se ${ocultas === 1 ? 'ocultó' : 'ocultaron'}. `
      + `Si esperabas encontrar algo y no está, NO concluyas que no existe: volvé a preguntar con ${parameter}: true.`,
  };
};
